<template>
  <div class="service-picker">
    <p v-if="actionable.length > 1 && !disabled" class="service-picker__bulk">
      <a href="#" @click.prevent="$emit('input', actionable.map((it) => it.name))">
        {{ $t('adminTitles.selectAll') }}
      </a>
      <span aria-hidden="true">/</span>
      <a href="#" @click.prevent="$emit('input', [])">{{ $t('adminTitles.clear') }}</a>
    </p>
    <ul class="service-picker__list">
      <li
        v-for="item in items"
        :key="item.name"
        :class="['service-picker__item', { 'service-picker__item--same': item.action === 'SAME' }]"
      >
        <el-checkbox
          v-if="item.action !== 'SAME'"
          class="service-picker__check"
          :value="value.includes(item.name)"
          :disabled="disabled"
          @change="toggle(item.name, $event)"
        >
          <span class="service-picker__name">{{ item.name }}</span>
          <span :class="['service-picker__tag', `service-picker__tag--${item.action.toLowerCase()}`]">
            {{ tag(item.action) }}
          </span>
        </el-checkbox>
        <!-- Nothing to decide: no box, but the same indent as the ones that have one. -->
        <span v-else class="service-picker__check service-picker__check--none">
          <span class="service-picker__name">{{ item.name }}</span>
          <span class="service-picker__tag service-picker__tag--same">{{ tag(item.action) }}</span>
        </span>
        <p v-if="detail(item)" class="service-picker__detail">{{ detail(item) }}</p>
      </li>
    </ul>
  </div>
</template>

<script>
import { isActionableService, serviceDetail } from '@/utils/carrierFormat';

const TAGS = {
  ADD: 'carriers.import.services.New',
  UPDATE: 'carriers.import.services.Changed',
  REMOVE: 'carriers.import.services.NotAtCarrier',
  SAME: 'carriers.import.services.Same',
};

/**
 * The carrier's services for one policy, one checkbox each.
 *
 * Nothing starts ticked: which services a policy gets is the agent's call,
 * service by service ("some we want, some no"), and only the ticked ones are
 * added, updated or removed. A service that is already the same has no box.
 *
 * `items` are `{ name, action, current, incoming }` (see `serviceItems`);
 * `value` is the ticked names, emitted whole on every change (`v-model`).
 */
export default {
  name: 'ServicePicker',
  props: {
    items: { type: Array, required: true },
    value: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
  },
  emits: ['input'],
  computed: {
    actionable() {
      return this.items.filter(isActionableService);
    },
  },
  methods: {
    tag(action) {
      return this.$t(TAGS[action]);
    },
    detail(item) {
      return serviceDetail(item);
    },
    toggle(name, on) {
      const rest = this.value.filter((n) => n !== name);
      this.$emit('input', on ? [...rest, name] : rest);
    },
  },
};
</script>

<style lang="scss" scoped>
.service-picker {
  &__bulk {
    display: flex;
    gap: 6px;
    font-size: 12.5px;
    color: var(--solid-dark-purple);
    margin-bottom: 6px;

    a {
      color: var(--solid-purple);
      text-decoration: underline;
      text-underline-offset: 2px;
    }
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__item {
    padding: 6px 0;
    border-bottom: 1px solid var(--solid-lavender-grey);

    &:last-child { border-bottom: 0; }

    &--same {
      color: var(--solid-dark-purple);
      opacity: 0.65;
    }
  }

  // Element keeps a checkbox's label on one line; a long service name wraps.
  &__check {
    display: flex;
    align-items: flex-start;
    white-space: normal;

    ::v-deep .el-checkbox__input { margin-top: 2px; }

    ::v-deep .el-checkbox__label {
      display: inline;
      color: var(--solid-black-purple);
      font-size: 13px;
      line-height: 1.4;
    }

    &--none {
      // The width of Element's box and the gap after it.
      padding-left: 24px;
      font-size: 13px;
      line-height: 1.4;
    }
  }

  &__name {
    overflow-wrap: anywhere;
  }

  &__tag {
    display: inline-block;
    margin-left: 8px;
    padding: 1px 8px;
    border-radius: 100px;
    font-size: 11px;
    font-weight: 500;
    vertical-align: 1px;
    white-space: nowrap;

    // Fixed tints, like the hold chips: the theme's greens are too bright for text.
    &--add {
      background: #e6f6e4;
      color: #2f7a28;
    }

    &--update {
      background: var(--solid-light-purple);
      color: var(--solid-purple);
    }

    &--remove {
      background: var(--solid-red-012);
      color: var(--solid-red);
    }

    &--same {
      background: var(--solid-lavender-grey);
      color: var(--solid-dark-purple);
    }
  }

  &__detail {
    margin: 2px 0 0 24px;
    font-size: 12.5px;
    line-height: 1.4;
    color: var(--solid-dark-purple);
    overflow-wrap: anywhere;
  }
}
</style>
